import { useState } from "react";
import { useTranslation } from "react-i18next";
import { forgotPassword } from "@/api";
import Modal from "../ui/Modal";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export default function ForgotPasswordModal({ isOpen, onClose, onSwitchToLogin }: ForgotPasswordModalProps) {
  const { t, i18n } = useTranslation("common");
  const isRTL = i18n.language === "ar";

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError(t("validEmailRequired"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      await forgotPassword({ email });
      setSuccess(true);
    } catch (err: any) {
      console.error(err);
      if (err.message === "email_send_failed") {
        setError(t("resetEmailSendFailed"));
      } else {
        setError(t("genericErrorRetry"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("forgotPasswordTitle")}
      maxWidth="md"
    >
      {success ? (
        <div className="text-center py-6 space-y-4">
          <div className="w-16 h-16 rounded-full bg-green-100 text-green-500 flex items-center justify-center mx-auto">
            <i className="ri-mail-check-line text-3xl" />
          </div>
          <h3 className="text-base font-bold text-gray-800">
            {t("resetLinkSentTitle")}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t("resetLinkSentDesc")}
          </p>
          <p className="text-xs text-gray-400">
            {t("resetLinkValidity")}
          </p>
          <button
            type="button"
            onClick={() => {
              handleClose();
              onSwitchToLogin();
            }}
            className="w-full py-3 bg-primary-400 hover:bg-primary-500 text-white rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
          >
            <i className="ri-login-box-line" />
            {t("backToLogin")}
          </button>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-500 rounded-xl text-xs flex items-center gap-2">
              <i className="ri-error-warning-line text-sm" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-4">
            <div className="w-14 h-14 rounded-full bg-primary-400/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-lock-password-line text-primary-400 text-2xl" />
            </div>
            <p className="text-sm text-gray-500 text-center leading-relaxed">
              {t("forgotPasswordDesc")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                {t("emailAddress")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@domain.com"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                dir="auto"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              {loading ? (
                <span>{t("sending")}</span>
              ) : (
                <>
                  <i className="ri-mail-send-line" />
                  <span>{t("sendResetLink")}</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-gray-500">
            {t("rememberPassword")}{" "}
            <button
              type="button"
              onClick={() => {
                handleClose();
                onSwitchToLogin();
              }}
              className="text-primary-400 hover:text-primary-500 font-semibold"
            >
              {t("login")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
